/**
 * Dialog "Tagesvorlagen" — Etappe 1 von reference/tagesvorlagen/TAGESVORLAGEN.md §3: Vorlagen der
 * Betriebsstätte anlegen, bearbeiten, duplizieren und löschen. Links die Liste (mit Suche), rechts der Editor
 * für die gewählte Vorlage: Name, vorgeschlagene Wochentage, Farbe, Einsatzort (gilt für alle Schichten),
 * Summenleiste, Schichten je Bereich und eine Notiz (bleibt an der Vorlage, wird nicht in den Bedarf übernommen).
 *
 * Bewusste Abweichungen von der Referenz: Posten haben keine eigene Qualifikation, daher ist der Default je
 * Zeile "beliebig". Die Farbauswahl ist dieselbe wie im Event-Editor (Vorschläge + eigene Farbe).
 * Das Einsetzen in Wochen/Tage folgt in Etappe 2.
 */

import { stammdatenService } from '../services/StammdatenService.js';
import { tagesvorlageService } from '../services/TagesvorlageService.js';
import { farbeFuerBereich } from '../util/bereichFarben.js';
import { zeigeKleineNachricht } from '../systemNachricht.js';
import { baueFarbAuswahl } from '../components/farbAuswahl.js';
import { bestaetige, istAppDialogOffen } from './bestaetigung.js';
import {
	baueStepper,
	bauePostenSelect,
	baueZeitInput,
	baueSelect,
	feld,
	textInput,
	textArea,
} from '../components/bedarfFelder.js';
import {
	WOCHENTAGE,
	STANDARD_FARBE,
	minuten,
	dauer,
	formatStunden,
	kennzahlen,
	schichtenText,
} from '../util/tagesvorlagen.js';

const FARB_VORSCHLAEGE = [STANDARD_FARBE, '#7a5af0', '#0f8a94', '#1f8a5b', '#b8791a', '#8499ae'];

let vorlagen = [];
// Die gerade bearbeitete Vorlage (neu: ohne _id) und ihr gespeicherter Stand (JSON) für die Änderungserkennung.
let entwurf = null;
let original = '';
let suche = '';
let stammdaten = { alleBereiche: [], bereiche: [], postenListe: [], qualifikationen: [], einsatzorte: [], betriebName: '' };
let escHandler = null;
// Aktualisiert Summen/Stunden nach Wertänderungen, ohne den Editor neu aufzubauen (Fokus bleibt erhalten).
let kennzahlUpdater = [];

function el(tag, className = '', text = '') {
	const e = document.createElement(tag);
	if (className) e.className = className;
	if (text) e.textContent = text;
	return e;
}

function leererEntwurf() {
	return { Name: '', Farbe: STANDARD_FARBE, Wochentage: [], EinsatzortID: '', Notiz: '', Schichten: [] };
}

/** Kopie einer gespeicherten Vorlage mit den im Editor verwendeten Feldern. */
function alsEntwurf(vorlage) {
	return {
		_id: vorlage._id,
		Name: vorlage.Name,
		Farbe: vorlage.Farbe || STANDARD_FARBE,
		Wochentage: [...vorlage.Wochentage],
		EinsatzortID: vorlage.EinsatzortID || '',
		Notiz: vorlage.Notiz || '',
		Schichten: vorlage.Schichten.map((s) => ({ ...s, QualifikationID: s.QualifikationID || '' })),
	};
}

function setzeEntwurf(neu, gespeichert = true) {
	entwurf = neu;
	// Ein nicht gespeichertes Duplikat gilt sofort als geändert (Rückfrage beim Verlassen).
	original = gespeichert ? JSON.stringify(entwurf) : '';
}

function istGeaendert() {
	return entwurf !== null && JSON.stringify(entwurf) !== original;
}

/** Bei ungespeicherten Änderungen nachfragen, ob sie verworfen werden dürfen. */
async function darfVerwerfen() {
	if (!istGeaendert()) return true;
	return bestaetige({
		titel: 'Ungespeicherte Änderungen verwerfen?',
		text: `Die Änderungen an "${entwurf.Name || 'Neue Tagesvorlage'}" sind noch nicht gespeichert.`,
		okText: 'Verwerfen',
	});
}

function entferneModal() {
	document.getElementById('tagesvorlagen-modal')?.remove();
	if (escHandler) {
		document.removeEventListener('keydown', escHandler);
		escHandler = null;
	}
}

async function schliesse() {
	if (!(await darfVerwerfen())) return;
	entferneModal();
}

function baueModalHuelle() {
	entferneModal();
	const modal = el('div', 'modal');
	modal.id = 'tagesvorlagen-modal';
	modal.style.display = 'block';
	modal.style.paddingTop = '4vh';
	modal.innerHTML = `
		<div class="modal-inhalt" style="width:1060px;max-height:92vh;">
			<div class="dlg tv-dlg">
				<div class="dlg-head">
					<div class="ic"><i class="bi bi-layers"></i></div>
					<div style="min-width:0;">
						<div class="ttl">Tagesvorlagen</div>
						<div class="sub" id="tv-sub"></div>
					</div>
					<i class="bi bi-x-lg x" id="tv-close"></i>
				</div>
				<div class="tv-wrap">
					<div class="tv-list">
						<div class="vsearch"><i class="bi bi-search"></i><input type="text" id="tv-suche" placeholder="Vorlage suchen …"></div>
						<div class="tv-items" id="tv-items"></div>
						<button type="button" class="tv-new" id="tv-neu"><i class="bi bi-plus-lg"></i>Neue Tagesvorlage</button>
					</div>
					<div class="tv-ed" id="tv-ed"></div>
				</div>
				<div class="dlg-foot">
					<button class="dbtn danger" id="tv-delete"><i class="bi bi-trash"></i>Vorlage löschen</button>
					<div class="sp"></div>
					<button class="dbtn" id="tv-dup"><i class="bi bi-copy"></i>Duplizieren</button>
					<button class="dbtn" id="tv-cancel">Schließen</button>
					<button class="dbtn primary" id="tv-save"><i class="bi bi-check-lg"></i>Speichern</button>
				</div>
			</div>
		</div>`;
	document.body.appendChild(modal);
	modal.addEventListener('mousedown', (e) => { if (e.target === modal) schliesse(); });
	modal.querySelector('#tv-close').addEventListener('click', schliesse);
	modal.querySelector('#tv-cancel').addEventListener('click', schliesse);
	modal.querySelector('#tv-save').addEventListener('click', speichern);
	modal.querySelector('#tv-delete').addEventListener('click', loeschen);
	modal.querySelector('#tv-dup').addEventListener('click', duplizieren);
	modal.querySelector('#tv-neu').addEventListener('click', neueVorlage);
	modal.querySelector('#tv-suche').addEventListener('input', (e) => {
		suche = e.target.value.trim().toLowerCase();
		rendereListe();
	});
	// Escape schließt nicht, solange darüber eine Bestätigung offen ist — die schließt Escape selbst.
	escHandler = (e) => { if (e.key === 'Escape' && !istAppDialogOffen()) schliesse(); };
	document.addEventListener('keydown', escHandler);
}

function aktualisiereKopfUndFuss() {
	const anzahl = vorlagen.length;
	document.getElementById('tv-sub').innerHTML = '<i class="bi bi-clipboard2-check"></i>Bedarf für einen ganzen Tag'
		+ '<span style="opacity:.5;">·</span><i class="bi bi-shop"></i><span id="tv-sub-bs"></span>'
		+ `<span style="opacity:.5;">·</span>${anzahl} ${anzahl === 1 ? 'Vorlage' : 'Vorlagen'}`;
	document.getElementById('tv-sub-bs').textContent = stammdaten.betriebName;
	const gespeichert = Boolean(entwurf?._id);
	document.getElementById('tv-delete').style.visibility = gespeichert ? 'visible' : 'hidden';
	document.getElementById('tv-dup').disabled = !gespeichert;
}

/** Wochentags-Pills (aktive Tage hervorgehoben). */
function baueTagePills(wochentage) {
	const tage = el('div', 'tv-days');
	WOCHENTAGE.forEach((label, i) => tage.appendChild(el('span', wochentage.includes(i + 1) ? 'on' : '', label)));
	return tage;
}

function baueListenEintrag(vorlage, aktiv) {
	const item = el('div', `tv-item${aktiv ? ' on' : ''}`);
	item.style.setProperty('--tvc', vorlage.Farbe || STANDARD_FARBE);
	item.appendChild(el('span', 'tn', vorlage.Name || 'Neue Tagesvorlage'));
	const k = kennzahlen(vorlage.Schichten);
	item.appendChild(el('span', 'tm', `${schichtenText(k.schichten)} · ${formatStunden(k.minuten)} h`));
	item.appendChild(baueTagePills(vorlage.Wochentage));
	return item;
}

function rendereListe() {
	const items = document.getElementById('tv-items');
	if (!items) return;
	items.innerHTML = '';
	// Eine neue, noch nicht gespeicherte Vorlage steht oben in der Liste.
	if (entwurf && !entwurf._id) {
		items.appendChild(baueListenEintrag(entwurf, true));
	}
	vorlagen
		.filter((v) => !suche || v.Name.toLowerCase().includes(suche))
		.forEach((vorlage) => {
			const item = baueListenEintrag(vorlage, vorlage._id === entwurf?._id);
			item.addEventListener('click', () => waehleVorlage(vorlage));
			items.appendChild(item);
		});
	if (vorlagen.length === 0 && !(entwurf && !entwurf._id)) {
		items.appendChild(el('div', 'tv-leer', 'Noch keine Tagesvorlagen angelegt.'));
	}
}

/** Mo–So: gewählt (.on) bzw. bereits von anderen Vorlagen belegt (.oth, Mehrfachbelegung ist erlaubt). */
function baueTagAuswahl() {
	const pick = el('div', 'tv-dpick');
	WOCHENTAGE.forEach((label, i) => {
		const tag = i + 1;
		const andere = vorlagen.filter((v) => v._id !== entwurf._id && v.Wochentage.includes(tag)).map((v) => v.Name);
		const pill = el('button', '', label);
		pill.type = 'button';
		const aktualisiere = () => {
			const gewaehlt = entwurf.Wochentage.includes(tag);
			pill.className = gewaehlt ? 'on' : (andere.length > 0 ? 'oth' : '');
			pill.title = andere.length > 0 ? `Auch vorgeschlagen in: ${andere.join(', ')}` : '';
		};
		pill.addEventListener('click', () => {
			entwurf.Wochentage = entwurf.Wochentage.includes(tag)
				? entwurf.Wochentage.filter((t) => t !== tag)
				: [...entwurf.Wochentage, tag].sort((a, b) => a - b);
			aktualisiere();
		});
		aktualisiere();
		pick.appendChild(pill);
	});
	return pick;
}

/** Badge in Bereichsfarbe (Summenleiste und Gruppenkopf). */
function baueBereichBadge(bereich, text) {
	const farbe = farbeFuerBereich(stammdaten.alleBereiche, bereich._id);
	const tag = el('span', 'needtag', text);
	tag.style.color = farbe;
	tag.style.background = `${farbe}1a`;
	return tag;
}

/** Summenleiste: Schichten · Stunden, je Bereich ein Tag, Zeitspanne erste–letzte Schicht. */
function fuelleSummenleiste(sum) {
	sum.innerHTML = '';
	const k = kennzahlen(entwurf.Schichten);
	sum.appendChild(el('span', 'big', `${schichtenText(k.schichten)} · ${formatStunden(k.minuten)} h`));
	bereicheDesEntwurfs().forEach((bereich) => {
		const anzahl = kennzahlen(entwurf.Schichten.filter((s) => s.BereichID === bereich._id)).schichten;
		sum.appendChild(baueBereichBadge(bereich, `${bereich.Bereich} ${anzahl}`));
	});
	sum.appendChild(el('span', 'sp'));
	const mitZeit = entwurf.Schichten.filter((s) => s.Von && s.Bis);
	if (mitZeit.length > 0) {
		const beginn = Math.min(...mitZeit.map((s) => minuten(s.Von)));
		const ende = Math.max(...mitZeit.map((s) => minuten(s.Von) + dauer(s)));
		const uhrzeit = (min) => `${String(Math.floor(min / 60) % 24).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`;
		sum.appendChild(el('span', 'tv-span', `${uhrzeit(beginn)} – ${uhrzeit(ende)}`));
	}
}

/** Bereiche, die in der Vorlage vorkommen — in der Reihenfolge der Unternehmens-Bereiche. */
function bereicheDesEntwurfs() {
	const ids = new Set(entwurf.Schichten.map((s) => s.BereichID));
	return stammdaten.bereiche.filter((b) => ids.has(b._id));
}

function neueSchicht(bereichID) {
	// Zeiten der letzten Schicht des Bereichs übernehmen, sonst ein Standard-Fenster.
	const letzte = [...entwurf.Schichten].reverse().find((s) => s.BereichID === bereichID);
	const posten = stammdaten.postenListe.find((p) => p.BereichID === bereichID);
	entwurf.Schichten.push({
		BereichID: bereichID,
		PostenID: letzte?.PostenID || posten?._id || '',
		Von: letzte?.Von || '10:00',
		Bis: letzte?.Bis || '18:00',
		Anzahl: 1,
		QualifikationID: '',
	});
	rendereEditor();
}

function baueSchichtZeile(schicht, bereich) {
	const row = el('div', 'tv-r');
	row.appendChild(bauePostenSelect({
		bereiche: [bereich],
		postenListe: stammdaten.postenListe,
		postenID: schicht.PostenID,
		className: 'ptsel',
		onChange: (info) => { schicht.PostenID = info.postenID; },
	}));
	row.appendChild(baueZeitInput(schicht.Von, (v) => { schicht.Von = v; aktualisiereKennzahlen(); }, 'ti'));
	row.appendChild(el('div', 'arrow', '–'));
	row.appendChild(baueZeitInput(schicht.Bis, (v) => { schicht.Bis = v; aktualisiereKennzahlen(); }, 'ti'));
	row.appendChild(baueStepper({ wert: schicht.Anzahl, min: 1, onChange: (n) => { schicht.Anzahl = n; aktualisiereKennzahlen(); } }));
	row.appendChild(baueSelect(
		stammdaten.qualifikationen.map((q) => ({ value: q._id, text: q.BezeichnungNeutral })),
		schicht.QualifikationID,
		'beliebig',
		(v) => { schicht.QualifikationID = v; },
		'ptsel q',
	));
	const hrs = el('div', 'hrs');
	kennzahlUpdater.push(() => { hrs.textContent = formatStunden(dauer(schicht) * schicht.Anzahl); });
	row.appendChild(hrs);
	const del = el('button', 'del');
	del.type = 'button';
	del.title = 'Schicht entfernen';
	del.innerHTML = '<i class="bi bi-trash"></i>';
	del.addEventListener('click', () => {
		entwurf.Schichten = entwurf.Schichten.filter((s) => s !== schicht);
		rendereEditor();
	});
	row.appendChild(del);
	return row;
}

function baueBereichsGruppe(bereich) {
	const grp = el('div', 'tv-grp');
	const kopf = el('div', 'tv-gh');
	kopf.appendChild(baueBereichBadge(bereich, bereich.Bereich));
	const meta = el('span', 'gm');
	kopf.appendChild(meta);
	kopf.appendChild(el('span', 'sp'));
	const add = el('button', 'addrow');
	add.type = 'button';
	add.innerHTML = '<i class="bi bi-plus-circle"></i>Schicht';
	add.addEventListener('click', () => neueSchicht(bereich._id));
	kopf.appendChild(add);
	grp.appendChild(kopf);

	const header = el('div', 'tv-r h');
	['Posten', 'Von', '', 'Bis', 'Anzahl', 'Qualifikation', 'Std.', ''].forEach((t) => header.appendChild(el('div', '', t)));
	grp.appendChild(header);

	const schichten = entwurf.Schichten.filter((s) => s.BereichID === bereich._id);
	kennzahlUpdater.push(() => {
		const k = kennzahlen(schichten);
		meta.textContent = `· ${schichtenText(k.schichten)} · ${formatStunden(k.minuten)} h`;
	});
	schichten.forEach((schicht) => grp.appendChild(baueSchichtZeile(schicht, bereich)));
	return grp;
}

/** Chips für Bereiche, die noch nicht in der Vorlage sind. */
function baueBereichHinzufuegen() {
	const vorhanden = new Set(entwurf.Schichten.map((s) => s.BereichID));
	const frei = stammdaten.bereiche.filter((b) => !vorhanden.has(b._id));
	if (frei.length === 0) return null;
	const wrap = el('div', 'tv-addb');
	wrap.appendChild(el('span', 'kvsug', 'Bereich hinzufügen'));
	frei.forEach((bereich) => {
		const chip = el('button', 'kvchip');
		chip.type = 'button';
		chip.innerHTML = '<i class="bi bi-plus"></i>';
		chip.appendChild(document.createTextNode(bereich.Bereich));
		chip.addEventListener('click', () => neueSchicht(bereich._id));
		wrap.appendChild(chip);
	});
	return wrap;
}

function aktualisiereKennzahlen() {
	kennzahlUpdater.forEach((fn) => fn());
}

function rendereEditor() {
	const ed = document.getElementById('tv-ed');
	if (!ed) return;
	ed.innerHTML = '';
	kennzahlUpdater = [];

	const reihe1 = el('div', 'tv-row3');
	const name = textInput(entwurf.Name, (v) => { entwurf.Name = v; }, 'z. B. Werktag');
	name.id = 'tv-name';
	name.maxLength = 100;
	reihe1.append(feld('Vorlagenname', name), feld('Vorgeschlagen für', baueTagAuswahl()));
	ed.appendChild(reihe1);

	const reihe2 = el('div', 'tv-row2');
	const farbe = baueFarbAuswahl({
		wert: entwurf.Farbe,
		vorschlaege: FARB_VORSCHLAEGE,
		onChange: (f) => { entwurf.Farbe = f; },
	});
	const einsatzort = baueSelect(
		stammdaten.einsatzorte.map((e) => ({ value: e._id, text: e.Bezeichnung })),
		entwurf.EinsatzortID,
		'– kein Einsatzort –',
		(v) => { entwurf.EinsatzortID = v; },
	);
	reihe2.append(feld('Farbe', farbe.element), feld('Einsatzort (für alle Schichten)', einsatzort));
	ed.appendChild(reihe2);

	const sum = el('div', 'tv-sum');
	kennzahlUpdater.push(() => fuelleSummenleiste(sum));
	ed.appendChild(sum);

	bereicheDesEntwurfs().forEach((bereich) => ed.appendChild(baueBereichsGruppe(bereich)));
	if (entwurf.Schichten.length === 0) {
		ed.appendChild(el('div', 'tv-leer', 'Noch keine Schichten — Bereich hinzufügen, um zu starten.'));
	}
	const hinzufuegen = baueBereichHinzufuegen();
	if (hinzufuegen) ed.appendChild(hinzufuegen);

	ed.appendChild(feld('Notiz (nur an der Vorlage)', textArea(entwurf.Notiz, (v) => { entwurf.Notiz = v; })));

	const hinweis = el('div', 'ghint plain');
	hinweis.innerHTML = '<i class="bi bi-info-circle"></i>Änderungen gelten für künftig eingesetzte Vorlagen. Bereits geplanter Bedarf bleibt unverändert.';
	ed.appendChild(hinweis);

	aktualisiereKennzahlen();
	aktualisiereKopfUndFuss();
}

function rendere() {
	rendereListe();
	rendereEditor();
}

async function waehleVorlage(vorlage) {
	if (vorlage._id === entwurf?._id) return;
	if (!(await darfVerwerfen())) return;
	setzeEntwurf(alsEntwurf(vorlage));
	rendere();
}

async function neueVorlage() {
	if (!(await darfVerwerfen())) return;
	setzeEntwurf(leererEntwurf());
	rendere();
	document.getElementById('tv-name')?.focus();
}

/** Kopie der gespeicherten Vorlage als neue, ungespeicherte Vorlage "… (Kopie)" ohne Wochentage. */
async function duplizieren() {
	if (!entwurf?._id) return;
	if (!(await darfVerwerfen())) return;
	const gespeichert = vorlagen.find((v) => v._id === entwurf._id);
	const kopie = alsEntwurf(gespeichert);
	delete kopie._id;
	kopie.Name = `${gespeichert.Name} (Kopie)`;
	kopie.Wochentage = [];
	setzeEntwurf(kopie, false);
	rendere();
	document.getElementById('tv-name')?.focus();
}

/** Prüft den Entwurf vor dem Speichern; meldet den ersten Fehler. */
function pruefeEntwurf() {
	if (!entwurf.Name.trim()) return 'Bitte einen Vorlagennamen angeben.';
	const doppelt = vorlagen.some((v) => v._id !== entwurf._id && v.Name.trim().toLowerCase() === entwurf.Name.trim().toLowerCase());
	if (doppelt) return `Eine Tagesvorlage mit dem Namen "${entwurf.Name.trim()}" gibt es bereits.`;
	if (entwurf.Schichten.some((s) => !s.PostenID)) return 'Bitte für jede Schicht einen Posten wählen.';
	if (entwurf.Schichten.some((s) => !s.Von || !s.Bis || s.Von === s.Bis)) return 'Bitte für jede Schicht ein gültiges Zeitfenster angeben.';
	return null;
}

async function speichern() {
	const fehler = pruefeEntwurf();
	if (fehler) {
		zeigeKleineNachricht(fehler, 0);
		return;
	}
	const gespeichert = await tagesvorlageService.speichereTagesvorlage(entwurf);
	if (!gespeichert) return;
	vorlagen = await tagesvorlageService.holeTagesvorlagen();
	setzeEntwurf(alsEntwurf(gespeichert));
	rendere();
}

async function loeschen() {
	if (!entwurf?._id) return;
	const ok = await bestaetige({
		titel: 'Tagesvorlage löschen?',
		text: `"${entwurf.Name}" wird gelöscht. Bereits eingesetzter Bedarf bleibt unverändert.`,
		okText: 'Löschen',
	});
	if (!ok) return;
	if (!(await tagesvorlageService.entferneTagesvorlage(entwurf._id))) return;
	vorlagen = await tagesvorlageService.holeTagesvorlagen();
	setzeEntwurf(vorlagen[0] ? alsEntwurf(vorlagen[0]) : leererEntwurf());
	rendere();
}

async function ladeStammdaten() {
	const unternehmen = await stammdatenService.getUnternehmensdaten();
	const postenListe = await stammdatenService.getPosten();
	stammdaten = {
		alleBereiche: unternehmen?.Bereiche || [],
		// Nur aktive Bereiche, die in dieser Betriebsstätte Posten haben
		bereiche: (unternehmen?.Bereiche || []).filter((b) => b.Verwenden && postenListe.some((p) => p.BereichID === b._id)),
		postenListe,
		qualifikationen: await stammdatenService.getQualifikationen(),
		einsatzorte: await stammdatenService.getEinsatzorte(),
		betriebName: unternehmen?.Betriebsstaette?.find((bs) => bs._id === stammdatenService.aktuelleBetriebsstaette)?.BetriebsstaetteName || '',
	};
}

/**
 * Öffnet den Dialog "Tagesvorlagen" und wählt die erste Vorlage (oder eine neue, falls es noch keine gibt).
 */
async function oeffneTagesvorlagen() {
	suche = '';
	await ladeStammdaten();
	vorlagen = await tagesvorlageService.holeTagesvorlagen();
	setzeEntwurf(vorlagen[0] ? alsEntwurf(vorlagen[0]) : leererEntwurf());
	baueModalHuelle();
	rendere();
}

export { oeffneTagesvorlagen };
